import React, { useState } from "react";
import {
  MOCK_DATE_STRIP,
  MOCK_FILTERS,
  MOCK_OFFERS,
  MOCK_SEARCH,
} from "../mock/flightEngineMockData";
import FlightSearchSummary from "./FlightSearchSummary";
import FlexibleDateStrip from "./FlexibleDateStrip";
import FlightResultsLayout from "./FlightResultsLayout";
import FlightFilterSidebar from "./FlightFilterSidebar";
import FlightResultsHeader from "./FlightResultsHeader";
import FlightOfferCard from "./FlightOfferCard";
import "./FlightResultsScreen.css";

/**
 * Greenfield Results presentation.
 * Accepts real ViewModels via props; Design Preview keeps mock defaults.
 * No SearchContext / BookingFlowContext / rawDeal / pricing math.
 */
export default function FlightResultsScreen({
  offers = MOCK_OFFERS,
  search = MOCK_SEARCH,
  /** null → Design Preview mock strip; [] → hide; array → live days (no invented prices) */
  dateStrip = null,
  filters = MOCK_FILTERS,
  selectedOfferId = null,
  /** @param {string} offerId */
  onSelectOfferId,
  /** @deprecated preview compat — prefer onSelectOfferId */
  onSelectOffer,
  onEditSearch,
  sortMode: sortModeProp,
  onSortChange,
  onFilterAction,
  onSelectDateDay,
  /** @param {"prev"|"next"} direction — page the strip and re-search */
  onShiftDateDays,
  loading = false,
  statusContent = null,
  resultCount = null,
}) {
  const [localSortMode, setLocalSortMode] = useState("recommended");
  const [localSelectedId, setLocalSelectedId] = useState(selectedOfferId);
  const [previewDateId, setPreviewDateId] = useState(
    MOCK_DATE_STRIP.find((d) => d.selected)?.id
  );

  const sortMode = sortModeProp ?? localSortMode;
  const handleSortChange = (next) => {
    if (typeof onSortChange === "function") onSortChange(next);
    else setLocalSortMode(next);
  };

  const days =
    dateStrip === null
      ? MOCK_DATE_STRIP.map((d) => ({
          ...d,
          selected: d.id === previewDateId,
        }))
      : Array.isArray(dateStrip)
        ? dateStrip
        : [];

  const handleSelectDay = (day) => {
    if (typeof onSelectDateDay === "function") {
      onSelectDateDay(day);
      return;
    }
    setPreviewDateId(day.id);
  };

  const handleShiftDays = (direction) => {
    if (typeof onShiftDateDays === "function") {
      onShiftDateDays(direction);
    }
  };

  const activeSelected = localSelectedId || selectedOfferId;
  const count =
    resultCount != null
      ? resultCount
      : filters?.resultCount != null
        ? filters.resultCount
        : Array.isArray(offers)
          ? offers.length
          : 0;

  const selectOfferId = (offerId) => {
    const id = String(offerId);
    setLocalSelectedId(id);
    if (typeof onSelectOfferId === "function") {
      onSelectOfferId(id);
      return;
    }
    const offer = (offers || []).find((o) => String(o?.id) === id);
    onSelectOffer?.(offer || { id });
  };

  return (
    <div className="fe2-results-page">
      <FlightSearchSummary search={search} onEditSearch={onEditSearch} />
      {days.length > 0 ? (
        <FlexibleDateStrip
          days={days}
          onSelectDay={handleSelectDay}
          onShiftDays={handleShiftDays}
        />
      ) : null}
      {statusContent}
      {loading ? (
        <p role="status">טוען תוצאות…</p>
      ) : null}
      <FlightResultsLayout
        sidebar={
          <FlightFilterSidebar filters={filters} onFilterAction={onFilterAction} />
        }
      >
        <FlightResultsHeader
          resultCount={count}
          search={search}
          sortMode={sortMode}
          onSortChange={handleSortChange}
        />
        <ul className="fe2-results-page__list">
          {(offers || []).map((offer) => (
            <li key={offer.id}>
              <FlightOfferCard
                offer={offer}
                selected={String(activeSelected) === String(offer.id)}
                onSelect={selectOfferId}
              />
            </li>
          ))}
        </ul>
      </FlightResultsLayout>
    </div>
  );
}
